import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, qs } from './client';
import type {
  AdminUserDto,
  CollectionDto,
  EntryDto,
  FileDto,
  FolderDetail,
  ItemSort,
  LogEntryDto,
  PageDto,
  PeerDto,
  ShareDto,
  ShareSubjectType,
  SortKey,
  TreeNode,
  UserDto,
} from './types';

/**
 * Query keys.
 *
 * Grouped by prefix so a mutation can invalidate a whole area at once -
 * uploading a file has to refresh the listing, the folder tree and the
 * enclosing folder's totals, and naming those individually at every call site
 * is how one of them ends up stale.
 */
export const keys = {
  me: ['me'] as const,
  browse: (params: Record<string, unknown>) => ['browse', params] as const,
  tree: ['tree'] as const,
  file: (id: string) => ['file', id] as const,
  folder: (id: string) => ['folder', id] as const,
  collections: ['collections'] as const,
  collection: (id: string, sort: string, page: number) => ['collection', id, sort, page] as const,
  shares: (type: string, id: string) => ['shares', type, id] as const,
  incoming: ['shares', 'incoming'] as const,
  peers: ['peers'] as const,
  adminUsers: ['admin', 'users'] as const,
  adminFiles: (params: Record<string, unknown>) => ['admin', 'files', params] as const,
  adminLogs: (params: Record<string, unknown>) => ['admin', 'logs', params] as const,
};

export interface Me {
  user: UserDto | null;
  sharedWithMe?: number;
}

export function useMe(): UseQueryResult<Me> {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<Me>('/api/me'),
    // The session outlives any screen, so re-checking on every focus would be
    // noise; an expired one is caught by the 401 handler instead.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export interface BrowseParams {
  folderId?: string | null;
  scope?: 'mine' | 'shared';
  q?: string;
  sort?: SortKey;
  page?: number;
}

export function useBrowse(params: BrowseParams) {
  const query = {
    folderId: params.folderId ?? null,
    scope: params.scope ?? 'mine',
    q: params.q ?? '',
    sort: params.sort ?? 'newest',
    page: params.page ?? 1,
  };
  return useQuery({
    queryKey: keys.browse(query),
    queryFn: () => api.get<PageDto<EntryDto>>(`/api/browse${qs(query)}`),
    // Keeps the previous page on screen while the next one loads, so paging
    // and typing in the search box do not blank the list.
    placeholderData: (previous) => previous,
  });
}

export interface SelectableIds {
  fileIds: string[];
  folderIds: string[];
  /** True when there were more matches than the server is willing to return. */
  capped: boolean;
}

/**
 * Resolves "everything the current listing covers" to a set of ids.
 *
 * A mutation rather than a query because it is an action the user takes, not
 * state the screen keeps: it should run when select-all is clicked, not
 * whenever the listing changes.
 */
export function useSelectableIds() {
  return useMutation({
    mutationFn: (params: { folderId?: string | null; scope?: string; q?: string }) =>
      api.get<SelectableIds>(`/api/browse/ids${qs(params)}`),
  });
}

export function useTree() {
  return useQuery({
    queryKey: keys.tree,
    queryFn: () => api.get<{ nodes: TreeNode[] }>('/api/tree'),
    staleTime: 30_000,
  });
}

export function useFolder(id: string | null) {
  return useQuery({
    queryKey: keys.folder(id ?? ''),
    queryFn: () => api.get<FolderDetail>(`/api/folders/${id}`),
    enabled: Boolean(id),
  });
}

export function useFile(id: string | null) {
  return useQuery({
    queryKey: keys.file(id ?? ''),
    queryFn: () => api.get<FileDto>(`/api/files/${id}`),
    enabled: Boolean(id),
  });
}

export function useCollections() {
  return useQuery({
    queryKey: keys.collections,
    queryFn: () => api.get<{ items: CollectionDto[] }>('/api/collections'),
  });
}

export interface CollectionPage extends PageDto<FileDto> {
  collection: CollectionDto;
}

export function useCollection(id: string, sort: ItemSort, page: number) {
  return useQuery({
    queryKey: keys.collection(id, sort, page),
    queryFn: () => api.get<CollectionPage>(`/api/collections/${id}${qs({ sort, page })}`),
    placeholderData: (previous) => previous,
  });
}

export function usePeers() {
  return useQuery({
    queryKey: keys.peers,
    queryFn: () => api.get<{ items: PeerDto[] }>('/api/peers'),
    staleTime: 5 * 60_000,
  });
}

export function useShares(subjectType: ShareSubjectType | null, subjectId: string | null) {
  return useQuery({
    queryKey: keys.shares(subjectType ?? '', subjectId ?? ''),
    queryFn: () =>
      api.get<{ items: ShareDto[] }>(`/api/shares${qs({ subjectType, subjectId })}`),
    enabled: Boolean(subjectType && subjectId),
  });
}

export function useIncomingShares() {
  return useQuery({
    queryKey: keys.incoming,
    queryFn: () => api.get<{ items: ShareDto[] }>('/api/shares/incoming'),
  });
}

/**
 * Anything that changes what is on screen invalidates broadly rather than
 * surgically. On a family-sized store a refetch is a few milliseconds, and
 * "the list did not update" is a far worse bug than one redundant request.
 */
export function useInvalidateContent() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['browse'] });
    void client.invalidateQueries({ queryKey: ['tree'] });
    void client.invalidateQueries({ queryKey: ['folder'] });
    void client.invalidateQueries({ queryKey: ['collections'] });
    void client.invalidateQueries({ queryKey: ['collection'] });
  };
}

export function useRenameFile() {
  const invalidate = useInvalidateContent();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name?: string; description?: string }) =>
      api.patch(`/api/files/${input.id}`, { name: input.name, description: input.description }),
    onSuccess: (_data, input) => {
      void client.invalidateQueries({ queryKey: keys.file(input.id) });
      invalidate();
    },
  });
}

export function useDeleteFile() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/files/${id}`),
    onSuccess: invalidate,
  });
}

export function useBulkFiles() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: { fileIds: string[]; action: 'delete' | 'move'; folderId?: string | null }) =>
      api.post<{ affected: number; skipped: number }>('/api/files/bulk', input),
    onSuccess: invalidate,
  });
}

export function useCreateFolder() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: { name: string; parentId?: string | null }) =>
      api.post<{ id: string }>('/api/folders', input),
    onSuccess: invalidate,
  });
}

/** Renames a folder, moves it to a new parent, or both. */
export function useUpdateFolder() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      description?: string;
      /** `null` moves it to the owner's root; omit to leave it where it is. */
      parentId?: string | null;
    }) =>
      api.patch(`/api/folders/${input.id}`, {
        name: input.name,
        description: input.description,
        ...(input.parentId !== undefined ? { parentId: input.parentId } : {}),
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteFolder() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/folders/${id}`),
    onSuccess: invalidate,
  });
}

/**
 * What a collection operation acts on: an explicit selection, or a folder
 * whose images the server resolves. Keeping them in one shape is what lets the
 * "add to album" dialog be the same dialog either way.
 */
export type AlbumSource = { fileIds: string[] } | { folderId: string };

export function useCreateCollection() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: { name: string; description?: string } & Partial<{ fileIds: string[]; folderId: string }>) =>
      api.post<{ id: string }>('/api/collections', input),
    onSuccess: invalidate,
  });
}

export function useUpdateCollection() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: { id: string; name?: string; description?: string; coverFileId?: string | null }) =>
      api.patch(`/api/collections/${input.id}`, {
        name: input.name,
        description: input.description,
        coverFileId: input.coverFileId,
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteCollection() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/collections/${id}`),
    onSuccess: invalidate,
  });
}

export function useCollectionItems() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: async (input: { id: string; op: 'add' | 'remove' } & AlbumSource) => {
      const { id, op, ...source } = input;
      if (op === 'add') {
        const result = await api.post<{ added: number; matched: number }>(
          `/api/collections/${id}/items`,
          source,
        );
        // `matched` is everything the source resolved to; `added` excludes what
        // was already in the album, so the difference is reportable.
        return { changed: result.added, matched: result.matched };
      }
      const result = await api.delete<{ removed: number }>(`/api/collections/${id}/items`, source);
      return { changed: result.removed, matched: result.removed };
    },
    onSuccess: invalidate,
  });
}

export function useGrantShare() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      subjectType: ShareSubjectType;
      subjectId: string;
      granteeId: string;
      role: 'viewer' | 'editor';
    }) => api.post<ShareDto>('/api/shares', input),
    onSuccess: (_data, input) => {
      void client.invalidateQueries({ queryKey: keys.shares(input.subjectType, input.subjectId) });
      void client.invalidateQueries({ queryKey: keys.incoming });
    },
  });
}

export function useRevokeShare() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/shares/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['shares'] });
      void client.invalidateQueries({ queryKey: ['browse'] });
      void client.invalidateQueries({ queryKey: ['tree'] });
    },
  });
}

/**
 * Starts a bulk download.
 *
 * Two steps: POST the selection to get a ticket, then navigate to it. A
 * download must be a real navigation for the browser to hand it to its
 * download manager, and a navigation cannot carry a list of two hundred ids.
 */
export function useZipDownload() {
  return useMutation({
    mutationFn: async (input: { fileIds?: string[]; folderId?: string; collectionId?: string }) => {
      const { token } = await api.post<{ token: string; count: number }>('/api/download/zip', input);
      window.location.assign(`/api/download/zip/${token}`);
    },
  });
}

// --- admin ----------------------------------------------------------------

export function useAdminUsers() {
  return useQuery({
    queryKey: keys.adminUsers,
    queryFn: () => api.get<{ items: AdminUserDto[] }>('/api/admin/users'),
  });
}

export function useAdminFiles(params: { q: string; sort: SortKey; page: number; state: string }) {
  return useQuery({
    queryKey: keys.adminFiles(params),
    queryFn: () => api.get<PageDto<FileDto>>(`/api/admin/files${qs(params)}`),
    placeholderData: (previous) => previous,
  });
}

export function useAdminLogs(params: {
  category: string;
  outcome?: string;
  userId?: string;
  page: number;
}) {
  return useQuery({
    queryKey: keys.adminLogs(params),
    queryFn: () => api.get<PageDto<LogEntryDto>>(`/api/admin/logs${qs(params)}`),
    placeholderData: (previous) => previous,
  });
}

export function useAdminUserMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input:
      | { op: 'create'; username: string; displayName: string; password: string; isAdmin: boolean }
      | { op: 'update'; id: string; username: string; displayName: string; password?: string; isAdmin?: boolean; isActive?: boolean }
      | { op: 'delete'; id: string }) => {
      if (input.op === 'create') {
        const { op: _op, ...body } = input;
        return api.post('/api/admin/users', body);
      }
      if (input.op === 'delete') return api.delete(`/api/admin/users/${input.id}`);
      const { op: _op, id, ...body } = input;
      return api.patch(`/api/admin/users/${id}`, body);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.adminUsers }),
  });
}

export function useAdminFileBulk() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { fileIds: string[]; action: 'soft-delete' | 'restore' | 'purge' }) =>
      api.post<{ affected: number }>('/api/admin/files/bulk', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin', 'files'] });
      void client.invalidateQueries({ queryKey: ['browse'] });
    },
  });
}
